import { Button, Container, FormControlLabel, FormGroup, Grid, Switch } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TemplateJSONEditor } from './template-json-editor';
import { TemplateLabelsForm } from './template-labels-form';
import { TemplateValuesForm } from './template-values-form';
import { AuthWithGoogleButton, isIapAuthRequired, resetIapAuthRequired } from 'security/iap';
import { setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { adminDeleteTemplate, adminLoadTemplate, adminPromoteTemplate, adminUpdateTemplateConfiguration } from 'store/sagas/sagas';
const boxToPoints = (box) => {
    return [
        { x: box.left, y: box.top },
        { x: box.left + box.width, y: box.top },
        {
            x: box.left + box.width,
            y: box.top + box.height
        },
        { x: box.left, y: box.top + box.height }
    ];
};
const toConfigPointsExtractionFields = (extractionFields) => {
    if (!extractionFields) {
        return [];
    }
    return Object.keys(extractionFields)
        .reduce((acc, key) => {
        const field = extractionFields[key];
        const [left, top, width, height] = field.rect;
        const points = boxToPoints({ left, top, width, height });
        return [
            ...acc,
            {
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                regex: field.regex || null,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                match: field.match || null,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                match_whole_line: field.match_whole_line || null,
                word: key,
                type: 'extraction',
                points
            }
        ];
    }, [])
        .toSorted((a, b) => {
        const aWord = a.word.toUpperCase();
        const bWord = b.word.toUpperCase();
        if (aWord < bWord) {
            return -1;
        }
        if (aWord > bWord) {
            return 1;
        }
        return 0;
    });
};
const toConfigPointsWords = (words) => {
    return Object.entries(words)
        .reduce((acc, [key, value]) => {
        const rects = value.rects.map((r, idx) => {
            const [left, top, width, height] = r;
            const points = boxToPoints({ left, top, width, height });
            const images = value.images ?? value.weights.map(() => false);
            return {
                weight: value.weights[idx],
                count: value.counts[idx],
                match_whole_line: value.match_whole_line,
                // match: configuration.words[key].match[idx],
                match: value.match && value.match.length > idx ? value.match[idx] : null,
                image: images[idx],
                word: key,
                type: 'words',
                points
            };
        });
        return [...acc, ...rects];
    }, [])
        .toSorted((a, b) => {
        const aWord = a.word.toUpperCase();
        const bWord = b.word.toUpperCase();
        if (aWord < bWord) {
            return -1;
        }
        if (aWord > bWord) {
            return 1;
        }
        return 0;
    });
};
const getRectanglePoints = (points) => {
    if (points.length < 4) {
        throw new Error('Must have at least 4 points');
    }
    const left = points[0].x;
    const top = points[0].y;
    const width = points[2].x - points[0].x;
    const height = points[3].y - points[0].y;
    return [left, top, width, height];
};
const isWordPointConfig = (obj) => {
    return obj.type === 'words';
};
const listPointsToConfig = (listPoints) => {
    const configObject = {
        words: {},
        extraction_fields: {}
    };
    for (const listPoint of listPoints) {
        if (isWordPointConfig(listPoint)) {
            const subList = configObject.words[listPoint.word];
            if (subList) {
                subList.counts = [...subList.counts, listPoint.count];
                subList.weights = [...subList.weights, listPoint.weight];
                subList.match_whole_line = !!listPoint.match;
                subList.images = [...(subList.images ?? []), listPoint.image];
                subList.rects = [
                    ...subList.rects,
                    getRectanglePoints(listPoint.points)
                ];
            }
            else {
                configObject.words[listPoint.word] = {
                    rects: [getRectanglePoints(listPoint.points)],
                    weights: [listPoint.weight],
                    images: [listPoint.image],
                    counts: [listPoint.count],
                    match: listPoint.match ? [listPoint.match] : []
                };
            }
        }
        else {
            const entry = {
                rect: getRectanglePoints(listPoint.points),
                regex: listPoint.regex
            };
            if (listPoint.match) {
                entry.match = listPoint.match;
            }
            configObject.extraction_fields[listPoint.word] = entry;
        }
    }
    return configObject;
};
const openDebugResults = (text) => {
    const win = window.open('', '_blank');
    if (!win)
        return;
    const pre = win.document.createElement('pre');
    pre.textContent = text;
    win.document.body.append(pre);
};
export const TemplateForm = () => {
    const { asyncDispatch, dispatch } = useGlobalState();
    const [drawingMode, setDrawingMode] = useState(false);
    const [drawRectangleMode, setDrawRectangleMode] = useState(false);
    const [newWord, setNewWord] = useState('');
    const [newExtractionWord, setNewExtractionWord] = useState('');
    const [extractionEditWord, setExtractionEditWord] = useState(null);
    const [editWord, setEditWord] = useState(null);
    const [listPoints, setListPoints] = useState([]);
    const [extractionListPoints, setExtractionListPoints] = useState([]);
    const [template, setTemplate] = useState(null);
    const [viewJson, setViewJson] = useState(false);
    const [config, setConfig] = useState(null);
    const [layout, setLayout] = useState('column');
    const [active, setActive] = useState(false);
    const { id } = useParams();
    useEffect(() => {
        resetIapAuthRequired();
        asyncDispatch(adminLoadTemplate(id))
            .then((loadedTemplate) => {
            try {
                setConfig(loadedTemplate.configuration);
                const relevantConfig = loadedTemplate.configuration ?? loadedTemplate.live_configuration;
                const wordPoints = relevantConfig
                    ? toConfigPointsWords(relevantConfig.words)
                    : [];
                const extractionPoints = relevantConfig
                    ? toConfigPointsExtractionFields(relevantConfig.extraction_fields)
                    : [];
                setListPoints(wordPoints);
                setExtractionListPoints(extractionPoints);
                setTemplate(loadedTemplate);
                setActive(loadedTemplate.active);
            }
            catch (error) {
                console.error(error);
                dispatch(setMessages([
                    {
                        value: 'Unable to load configuration properties',
                        severity: 'error'
                    }
                ]));
            }
        })
            .catch(console.error);
    }, []);
    const addNewWord = () => {
        const newPoints = listPoints;
        const newIndex = listPoints.length;
        newPoints.push({
            count: 1,
            image: false,
            match: '',
            match_whole_line: false,
            points: [],
            type: 'words',
            weight: 1,
            word: newWord
        });
        setListPoints(newPoints);
        setEditWord(newIndex);
        setDrawRectangleMode(true);
    };
    const addNewExtractionWord = () => {
        const newPoints = extractionListPoints;
        const newIndex = extractionListPoints.length;
        newPoints.push({
            match: '',
            points: [],
            type: 'extraction',
            match_whole_line: null,
            regex: '',
            word: newExtractionWord
        });
        setExtractionListPoints(newPoints);
        setExtractionEditWord(newIndex);
        setDrawRectangleMode(true);
    };
    const overwriteRectangle = (index, points) => {
        const newPoints = [...listPoints];
        newPoints[index].points = points;
        if (points.length === 4) {
            setEditWord(null);
            setDrawRectangleMode(false);
        }
        setListPoints(newPoints);
    };
    const overwriteExtractionRectangle = (index, points) => {
        const newPoints = [...extractionListPoints];
        newPoints[index].points = points;
        if (points.length === 4) {
            setExtractionEditWord(null);
            setDrawRectangleMode(false);
        }
        setExtractionListPoints(newPoints);
    };
    const handleUpdateTemplate = () => {
        if (!template) {
            return;
        }
        const newPoints = listPointsToConfig([
            ...listPoints,
            ...extractionListPoints
        ]);
        const updatedConfig = {
            ...config,
            ...newPoints
        };
        setConfig(updatedConfig);
        asyncDispatch(adminUpdateTemplateConfiguration({
            sid: template.sid,
            configuration: updatedConfig,
            active
        }))
            .then(() => {
            dispatch(setMessages({
                value: `Template updated`,
                severity: 'success'
            }));
        })
            .catch(console.error);
    };
    const deleteTemplate = () => {
        if (!template) {
            return;
        }
        // eslint-disable-next-line no-alert
        if (!confirm('Are you sure you want to delete this template?')) {
            return;
        }
        asyncDispatch(adminDeleteTemplate(template.id))
            .then(() => {
            dispatch(setMessages({
                value: `Template deleted. Reload page or go back to the templates dashboard to confirm`,
                severity: 'success'
            }));
        })
            .catch(console.error);
    };
    const promoteTemplate = () => {
        if (!template) {
            return;
        }
        asyncDispatch(adminPromoteTemplate(template.sid))
            .then(() => {
            dispatch(setMessages({
                value: `Template promoted`,
                severity: 'success'
            }));
        })
            .catch(console.error);
    };
    const handleAdditionalValueChange = useCallback((value) => {
        if (Object.keys(value)[0] === 'presetExpiration') {
            setConfig((prevState) => ({
                ...prevState,
                presetExpiration: {
                    ...prevState.presetExpiration,
                    ...value.presetExpiration
                }
            }));
        }
        else {
            setConfig((prevState) => ({ ...prevState, ...value }));
        }
    }, []);
    const toggleValues = {
        has_PDF417_back: config?.has_PDF417_back ?? false,
        pdf417Reconcile: config?.pdf417Reconcile ?? false,
        lastname_first: config?.lastname_first ?? false,
        comma_separated_name: config?.comma_separated_name ?? false,
        case_separated_name: config?.case_separated_name ?? false,
        is_paper: config?.is_paper ?? false
    };
    const dropdownValues = {
        values: {
            require_side: config?.require_side ?? null,
            side: config?.side ?? null,
            presetExpirationDay: config?.presetExpiration?.day ?? null,
            presetExpirationMonth: config?.presetExpiration?.month ?? null,
            dateFormat: config?.dateFormat ?? null
        },
        options: {
            require_side: ['back'],
            side: ['back'],
            presetExpirationDay: Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, '0')),
            presetExpirationMonth: Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')),
            dateFormat: ['us', 'intl']
        }
    };
    return (React.createElement("div", null,
        isIapAuthRequired() && (React.createElement(Container, null,
            "This page is protected by IAP - ",
            React.createElement(AuthWithGoogleButton, null))),
        React.createElement("h1", null, "Template Editor"),
        template ? (React.createElement("div", null,
            React.createElement(FormGroup, { row: true },
                React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: drawingMode, onChange: () => {
                            setDrawingMode(!drawingMode);
                        } }), label: "Extraction mode" }),
                React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: layout === 'row', onChange: () => {
                            setLayout((curr) => (curr === 'row' ? 'column' : 'row'));
                        } }), label: "Row layout" })),
            drawingMode ? (React.createElement(TemplateValuesForm, { drawRectangleMode: drawRectangleMode, dropdownValues: dropdownValues, editWord: extractionEditWord, handleAdditionalValueChange: handleAdditionalValueChange, layout: layout, listPoints: extractionListPoints, newWord: newExtractionWord, onAddNewWord: addNewExtractionWord, overwriteRectangle: overwriteExtractionRectangle, photoDimensions: template.photo_dimensions, photoUrl: template.photo_url, setDrawRectangleMode: setDrawRectangleMode, setEditWord: setExtractionEditWord, setListPoints: setExtractionListPoints, setNewWord: setNewExtractionWord, toggleValues: toggleValues })) : (React.createElement(TemplateLabelsForm, { drawRectangleMode: drawRectangleMode, editWord: editWord, layout: layout, listPoints: listPoints, newWord: newWord, onAddNewWord: addNewWord, overwriteRectangle: overwriteRectangle, photoDimensions: template.photo_dimensions, photoUrl: template.photo_url, setDrawRectangleMode: setDrawRectangleMode, setEditWord: setEditWord, setListPoints: setListPoints, setNewWord: setNewWord })),
            viewJson && config && React.createElement(TemplateJSONEditor, { values: config }))) : null,
        React.createElement(Grid, { container: true, spacing: 1 },
            React.createElement(Grid, { item: true },
                React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: active, onChange: (_, checked) => {
                            setActive(checked);
                        } }), label: "Active" })),
            React.createElement(Grid, { item: true },
                React.createElement(Button, { "data-cy": "json-editor", onClick: () => {
                        setViewJson((prevState) => !prevState);
                    } }, viewJson ? 'Close JSON' : 'View JSON')),
            React.createElement(Grid, { item: true },
                React.createElement(Button, { onClick: promoteTemplate }, "Promote")),
            React.createElement(Grid, { item: true },
                React.createElement(Button, { onClick: () => {
                        if (!template) {
                            return;
                        }
                        openDebugResults(JSON.stringify(template, null, 1));
                    } }, "Download")),
            React.createElement(Grid, { item: true },
                React.createElement(Button, { onClick: handleUpdateTemplate }, "Update")),
            React.createElement(Grid, { item: true },
                React.createElement(Button, { color: "secondary", onClick: deleteTemplate, variant: "contained" }, "Delete")))));
};
